import { z } from 'zod'

type BlankPolicy = Readonly<{ blank: 'allow' | 'forbid' }>

function string(policy: BlankPolicy): z.ZodString {
  const schema = z.string()
  return policy.blank === 'allow' ? schema : schema.trim().min(1)
}

function array<TItem extends z.ZodType>(
  item: TItem,
  policy: BlankPolicy,
): z.ZodArray<TItem> {
  const schema = z.array(item)
  return policy.blank === 'allow' ? schema : schema.min(1)
}

function object<TShape extends z.ZodRawShape>(
  shape: TShape,
  policy: BlankPolicy,
): z.ZodObject<TShape> | z.ZodPipe<z.ZodObject<TShape>, z.ZodTransform<z.output<z.ZodObject<TShape>>, z.output<z.ZodObject<TShape>>>> {
  const schema = z.strictObject(shape)
  if (policy.blank === 'allow') return schema

  return schema.transform((value, context) => {
    if (Object.keys(value).length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Object must not be empty',
      })
    }
    return value
  })
}

export const responseValue = {
  array,
  object,
  string,
} as const

export type ResponseContract<TSchema extends z.ZodType = z.ZodType> = Readonly<{
  name: string
  schema: TSchema
  safeValuePaths: readonly string[]
}>

export function defineResponseContract<TSchema extends z.ZodType>(definition: {
  name: string
  schema: TSchema
  safeValuePaths?: readonly string[]
}): ResponseContract<TSchema> {
  return {
    ...definition,
    safeValuePaths: definition.safeValuePaths ?? [],
  }
}

export function extractSafeValues(
  input: unknown,
  paths: readonly string[],
): Readonly<Record<string, unknown>> {
  const values: Record<string, unknown> = {}

  const visit = (
    value: unknown,
    segments: readonly string[],
    resolved: readonly string[],
  ): void => {
    if (segments.length === 0) {
      values[resolved.join('.')] = value
      return
    }

    const [segment, ...rest] = segments
    if (segment === '*') {
      if (Array.isArray(value)) {
        value.forEach((child, index) => visit(child, rest, [...resolved, String(index)]))
      } else if (value && typeof value === 'object') {
        for (const [key, child] of Object.entries(value)) {
          visit(child, rest, [...resolved, key])
        }
      }
      return
    }

    if (value && typeof value === 'object' && Object.hasOwn(value, segment)) {
      visit((value as Record<string, unknown>)[segment], rest, [...resolved, segment])
    }
  }

  for (const path of paths) visit(input, path.split('.'), [])
  return values
}
